/**
 * Frontend Navigation Builder
 * 
 * Builds role-based navigation based on the effective user role.
 * Handles impersonation correctly - when impersonating, uses the impersonated user's role.
 */

import { 
  BarChart3, 
  Building, 
  Building2, 
  FileText, 
  ClipboardCheck, 
  Users, 
  Wrench,
  Mail,
  FileBarChart,
  CheckSquare,
  Award
} from "lucide-react";
import type { Role } from "../auth/roles";

export interface NavItem {
  icon: any; // Lucide React icon component
  label: string;
  href: string;
  testId?: string;
}

export interface NavigationStructure {
  main: NavItem[];
  admin: NavItem[];
}

export interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  role: string;
  organization_id?: number | null;
  home_id?: number | null;
  is_superadmin?: boolean;
}

export interface OrgFeatures {
  [key: string]: any;
}

/**
 * Build navigation structure based on effective user role and context
 * 
 * @param user - The effective user (who we're acting as during impersonation)
 * @param orgFeatures - Organization feature flags
 * @param isImpersonating - Whether currently impersonating another user
 * @returns Navigation structure with main and admin sections
 */
export function buildNav(
  user: User | null, 
  orgFeatures: OrgFeatures | null = null, 
  isImpersonating: boolean = false
): NavigationStructure {
  if (!user) {
    return { main: [], admin: [] };
  }

  const role = user.role as Role;
  
  // For Developer and PlatformManager: if not impersonating, they get special platform navigation
  // If impersonating, they follow the rules of the role they're impersonating
  if ((role === 'Developer' || role === 'PlatformManager') && !isImpersonating) {
    return buildPlatformNavigation();
  }

  // For all other roles, or when Developer/PlatformManager are impersonating
  return buildRoleBasedNavigation(role);
}

/**
 * Platform navigation for Developer and PlatformManager (when not impersonating)
 */
function buildPlatformNavigation(): NavigationStructure {
  return {
    main: [
      {
        icon: BarChart3,
        label: "Dashboard",
        href: "/dashboard",
        testId: "nav-dashboard"
      },
      {
        icon: Building,
        label: "Organizations", 
        href: "/admin/organizations",
        testId: "nav-organizations"
      },
      {
        icon: Building2,
        label: "Care Homes",
        href: "/homes",
        testId: "nav-homes"
      },
      {
        icon: FileText,
        label: "Template Management",
        href: "/templates/library",
        testId: "nav-templates"
      }
    ],
    admin: [
      {
        icon: Users,
        label: "User Management",
        href: "/admin/users",
        testId: "nav-users"
      },
      {
        icon: Mail,
        label: "Demo Enquiries",
        href: "/enquiries",
        testId: "nav-enquiries"
      }
    ]
  };
}

/**
 * Role-based navigation for organization and home users
 */
function buildRoleBasedNavigation(role: Role): NavigationStructure {
  const navigation: NavigationStructure = {
    main: [],
    admin: []
  };

  // Dashboard is available to all roles
  navigation.main.push({
    icon: BarChart3,
    label: "Dashboard",
    href: "/dashboard",
    testId: "nav-dashboard"
  });

  // Role-specific main navigation
  switch (role) {
    case 'OrgManager':
      navigation.main.push(
        {
          icon: Building2,
          label: "Care Homes",
          href: "/homes",
          testId: "nav-homes"
        },
        {
          icon: FileText,
          label: "Audit Management",
          href: "/templates",
          testId: "nav-templates"
        },
        {
          icon: ClipboardCheck,
          label: "Audits",
          href: "/audits",
          testId: "nav-audits"
        },
        {
          icon: CheckSquare,
          label: "Actions",
          href: "/actions",
          testId: "nav-actions"
        },
        {
          icon: FileBarChart,
          label: "Reports",
          href: "/reports",
          testId: "nav-reports"
        },
        {
            icon: Award,
            label: "Good Practices",
            href: "/good-practices",
            testId: "nav-good-practices"
          }
      );
      navigation.admin.push(
        {
          icon: Building,
          label: "My Organization",
          href: "/admin/my-organization",
          testId: "nav-my-org"
        },
        {
          icon: Users,
          label: "User Management",
          href: "/admin/users",
          testId: "nav-users"
        }
      );
      break;

    case 'OrgAdmin':
      navigation.main.push(
        {
          icon: Building2,
          label: "Care Homes",
          href: "/homes",
          testId: "nav-homes"
        },
        {
          icon: FileText,
          label: "Audit Management",
          href: "/templates",
          testId: "nav-templates"
        },
        {
          icon: ClipboardCheck,
          label: "Audits",
          href: "/audits",
          testId: "nav-audits"
        },
        {
          icon: FileBarChart,
          label: "Reports",
          href: "/reports",
          testId: "nav-reports"
        },
        {
            icon: Award,
            label: "Good Practices",
            href: "/good-practices",
            testId: "nav-good-practices"
          }
        // NO Audits for OrgAdmin
      );
      navigation.admin.push({
        icon: Users,
        label: "User Management",
        href: "/admin/users",
        testId: "nav-users"
      });
      break;

    case 'HomeManager':
      navigation.main.push(
        {
          icon: FileText,
          label: "Audit Management",
          href: "/templates",
          testId: "nav-templates"
        },
        {
          icon: ClipboardCheck,
          label: "Audits",
          href: "/audits",
          testId: "nav-audits"
        },
        {
          icon: CheckSquare,
          label: "Actions",
          href: "/actions",
          testId: "nav-actions"
        },
        {
          icon: FileBarChart,
          label: "Reports",
          href: "/reports",
          testId: "nav-reports"
        },
        {
            icon: Award,
            label: "Good Practices",
            href: "/good-practices",
            testId: "nav-good-practices"
          }
      );
      navigation.admin.push(
        {
          icon: Building2,
          label: "My Home",
          href: "/admin/my-home",
          testId: "nav-my-home"
        },
        {
          icon: Users,
          label: "User Management",
          href: "/admin/users",
          testId: "nav-users"
        }
      );
      break;

    case 'HomeAdmin':
      navigation.main.push(
        {
          icon: FileText,
          label: "Audit Management",
          href: "/templates",
          testId: "nav-templates"
        },
        {
          icon: ClipboardCheck,
          label: "Audits",
          href: "/audits",
          testId: "nav-audits"
        },
        {
          icon: FileBarChart,
          label: "Reports",
          href: "/reports",
          testId: "nav-reports"
        },
        {
            icon: Award,
            label: "Good Practices",
            href: "/good-practices",
            testId: "nav-good-practices"
          }
      );
      navigation.admin.push({
        icon: Users,
        label: "User Management",
        href: "/admin/users",
        testId: "nav-users"
      });
      break;

    case 'HomeStaff':
      navigation.main.push(
        {
          icon: FileText,
          label: "Audit Management",
          href: "/templates",
          testId: "nav-templates"
        },
        {
          icon: ClipboardCheck,
          label: "Audits",
          href: "/audits",
          testId: "nav-audits"
        }
      );
      // NO admin section for HomeStaff
      break;

    case 'Developer':
    case 'PlatformManager':
      // These cases are handled above when not impersonating
      // If we reach here, they are impersonating someone else
      // This shouldn't happen based on the role system, but as a fallback:
      navigation.main.push(
        {
          icon: Building2,
          label: "Care Homes",
          href: "/homes",
          testId: "nav-homes"
        },
        {
          icon: FileText,
          label: "Templates",
          href: "/templates",
          testId: "nav-templates"
        }
      );
      break;

    default:
      // Fallback for unknown roles
      navigation.main.push(
        {
          icon: FileText,
          label: "Templates",
          href: "/templates",
          testId: "nav-templates"
        }
      );
      break;
  }

  return navigation;
}

/**
 * Helper function to check if admin section should be shown
 */
export function hasAdminAccess(user: User | null): boolean {
  if (!user) return false;
  
  const role = user.role as Role;
  
  // Admin access for roles that can manage users
  return ['Developer', 'PlatformManager', 'OrgManager', 'OrgAdmin', 'HomeManager', 'HomeAdmin'].includes(role);
}

/**
 * Get the effective user for navigation purposes
 * During impersonation, this should be the impersonated user, not the actor
 */
export function getEffectiveUser(session: any): User | null {
  if (!session) return null;
  
  // During impersonation, use the impersonated user for navigation
  if (session.isImpersonating && session.user) {
    return session.user;
  }
  
  // Otherwise use the actual user
  return session.user || null;
}